import { PlusIcon } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import type {
  CloudAllowanceStatus,
  CloudWorkspace,
  ConnectDesktopBridge,
} from "@afar-research/contracts";
import { EnvironmentId } from "@afar-research/contracts";
import {
  addConnectEnvironment,
  addedConnectEnvironments,
  connectAccountKey,
} from "../../connection/connectEnvironments";
import { reconnectCloudWorkspace } from "../../connection/cloudWorkspaceRegistration";
import { checkConnectSnapshot, useConnectSnapshot } from "../../connection/connectSnapshot";
import { useClientSettings } from "../../hooks/useSettings";
import { randomUUID } from "../../lib/utils";
import { Button } from "../ui/button";
import { Spinner } from "../ui/spinner";
import {
  CLOUD_WORKSPACE_REFRESH_MS,
  cloudCreateBlocker,
  cloudResizeBlocker,
  type CloudWorkspaceRequest,
  defaultSize,
  describeCloudUsageLine,
  describeSize,
  describeWorkspaceRow,
  describeWorkspaceSpec,
  isWorkspaceUsedHere,
  OPEN_TIMEOUT_MS,
  openProgress,
  resizeChange,
  shouldRefreshCloudWorkspaces,
  shouldWarnAboutRetention,
  sizeChoices,
  workspacesToConnect,
} from "./CloudWorkspaces.logic";
import { useNetworkBridge } from "./networkMachineConnections";

export type CloudBridge = Required<
  Pick<
    ConnectDesktopBridge,
    | "cloudWorkspaces"
    | "createCloudWorkspace"
    | "cloudWorkspaceAction"
    | "cloudAllowance"
    | "cloudAccounts"
    | "cloudAllowanceHistory"
    | "adjustCloudAllowance"
    | "changeCloudSubscription"
  >
> &
  Pick<ConnectDesktopBridge, "getStatus">;
export function cloudBridge(bridge: ConnectDesktopBridge | undefined): CloudBridge | undefined {
  if (
    !bridge?.cloudWorkspaces ||
    !bridge.createCloudWorkspace ||
    !bridge.cloudWorkspaceAction ||
    !bridge.cloudAllowance ||
    !bridge.cloudAccounts ||
    !bridge.cloudAllowanceHistory ||
    !bridge.adjustCloudAllowance ||
    !bridge.changeCloudSubscription
  )
    return undefined;
  return {
    getStatus: () => bridge.getStatus(),
    cloudAllowance: () => bridge.cloudAllowance!(),
    cloudAccounts: (search) => bridge.cloudAccounts!(search),
    cloudAllowanceHistory: (id) => bridge.cloudAllowanceHistory!(id),
    adjustCloudAllowance: (request) => bridge.adjustCloudAllowance!(request),
    changeCloudSubscription: (request) => bridge.changeCloudSubscription!(request),
    cloudWorkspaces: () => bridge.cloudWorkspaces!(),
    createCloudWorkspace: (request) => bridge.createCloudWorkspace!(request),
    cloudWorkspaceAction: (request) => bridge.cloudWorkspaceAction!(request),
  };
}

/**
 * Starts a run when none is live, waits until it is reachable, then adds the workspace to this
 * device as an Environment. `read` lists the account's workspaces, so a caller showing the list
 * sees each step; `isActive` lets a caller that went away stop waiting without reporting back.
 * Resolves whether the workspace was added. Fails with a sentence the user can read.
 */
export async function startAndConnectCloudWorkspace(
  bridge: Pick<CloudBridge, "getStatus" | "cloudWorkspaceAction">,
  workspace: CloudWorkspace,
  options: {
    readonly read: () => Promise<ReadonlyArray<CloudWorkspace>>;
    readonly isActive: () => boolean;
  },
): Promise<boolean> {
  const account = await bridge.getStatus();
  if (account.state !== "signed-in") throw new Error("The account is signed out. Sign in again.");
  if (!options.isActive()) return false;
  if (workspace.state !== "running")
    await bridge.cloudWorkspaceAction({
      id: workspace.id,
      action: "open",
      expectedRevision: workspace.revision,
      requestId: randomUUID(),
    });
  const deadline = Date.now() + OPEN_TIMEOUT_MS;
  let ready: CloudWorkspace;
  for (;;) {
    if (!options.isActive()) return false;
    // Every read reaches Connect's database; a hidden page waits without asking, so a start left
    // in a background tab does not keep that database awake.
    if (document.visibilityState === "hidden") {
      await new Promise((resolve) => setTimeout(resolve, CLOUD_WORKSPACE_REFRESH_MS));
      continue;
    }
    const current = (await options.read()).find((row) => row.id === workspace.id);
    const step = openProgress(current);
    if (step === "ready" && current) {
      ready = current;
      break;
    }
    if (step === "failed") throw new Error("The workspace did not start. Try again.");
    if (Date.now() > deadline)
      throw new Error(
        "The workspace is taking longer than usual to start. Check back in a minute.",
      );
    await new Promise((resolve) => setTimeout(resolve, CLOUD_WORKSPACE_REFRESH_MS));
  }
  const status = await bridge.getStatus();
  if (!options.isActive()) return false;
  if (status.state !== "signed-in" || status.activeAccountId !== account.activeAccountId)
    throw new Error("The active account changed. Reopen this page.");
  // Once adding has begun it finishes, so the Environment is never left half-registered.
  await addConnectEnvironment(
    connectAccountKey(status.activeAccountId),
    EnvironmentId.make(workspace.id),
  );
  // Every surface listing workspaces follows the snapshot this wait just read.
  reconnectCloudWorkspace(ready);
  return true;
}

const LOAD_ERROR = "Could not load Cloud Workspaces.";

/** Turns a failed Connect request into a sentence; only our own messages are shown as they are. */
export function describeCloudFailure(cause: unknown): string {
  return cause instanceof Error && cause.message.startsWith("The ")
    ? cause.message
    : "Connect could not complete the request. Retry to check its status first.";
}

/** What `useCloudWorkspaces` knows and can do; null lists mean the first read has not answered. */
export interface CloudWorkspacesState {
  readonly workspaces: ReadonlyArray<CloudWorkspace> | null;
  readonly allowance: CloudAllowanceStatus | null;
  /** The workspaces that still exist, each one Connect can start using here or select again. */
  readonly connectable: ReadonlyArray<CloudWorkspace> | null;
  readonly requests: ReadonlyMap<string, CloudWorkspaceRequest>;
  readonly creating: string | null;
  readonly error: string | null;
  readonly retry: () => void;
  /** The workspaces this device has in its Connections. */
  readonly usedHere: ReadonlyArray<string>;
  readonly connect: (workspace: CloudWorkspace) => void;
  /** Starts a workspace without adding it to this device's Connections. */
  readonly start: (workspace: CloudWorkspace) => void;
  /** Stops or deletes a workspace; resolves whether Connect accepted it. */
  readonly act: (workspace: CloudWorkspace, kind: "stop" | "delete") => Promise<boolean>;
  /** Changes a stopped workspace's size, grows its disk, or both. */
  readonly resize: (
    workspace: CloudWorkspace,
    change: { readonly size?: string; readonly diskGb?: number },
  ) => void;
  /**
   * Creates a workspace, stopped, on the account's network; this device adds it to its Connections
   * only when asked to connect. Resolves whether Connect created it.
   */
  readonly create: (input: {
    label: string;
    image: string;
    size: string;
    diskGb: number;
    requestId: string;
  }) => Promise<boolean>;
}

/**
 * The account's Cloud Workspaces and their lifecycle, for every surface that changes them. The list
 * is the Connect snapshot, checked when the page is used and read again after every change made
 * here; while a workspace is changing it is read every few seconds, only while the page is in view.
 * Every change carries the revision it was decided against and a request id.
 */
export function useCloudWorkspaces(
  bridge: CloudBridge | undefined,
  options: {
    /** Told when this device starts using a workspace, not when one it uses is started again. */
    readonly onAdded?: (label: string) => void;
    /** Told after a refused request, so a caller can learn whether the account itself changed. */
    readonly onFailed?: () => void;
  } = {},
): CloudWorkspacesState {
  const snapshot = useConnectSnapshot();
  const workspaces = snapshot?.workspaces ?? null;
  const allowance = snapshot?.allowance ?? null;
  const accountKey = snapshot?.accountKey ?? null;
  const added = useClientSettings((settings) =>
    accountKey === null ? undefined : addedConnectEnvironments(settings, accountKey),
  );
  const [requests, setRequests] = useState<ReadonlyMap<string, CloudWorkspaceRequest>>(
    () => new Map(),
  );
  const [creating, setCreating] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const callbacks = useRef(options);
  callbacks.current = options;
  // Work started here outlives an unmounted caller on the server, never in this hook: once the
  // caller is gone a start stops waiting, sends nothing new, and reports nothing back.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  // Every read goes through the snapshot, which shares one request between callers that arrive
  // together and never lets an answer read before a change stand in for one read after it. Every
  // surface showing the list follows the snapshot, so nothing else needs telling.
  const readWorkspaces = useCallback(
    async () =>
      bridge
        ? ((await checkConnectSnapshot({ force: true, bridge, workspacesOnly: true }))
            ?.workspaces ?? [])
        : [],
    [bridge],
  );
  const refresh = useCallback(async () => {
    if (bridge) await checkConnectSnapshot({ force: true, bridge });
  }, [bridge]);
  useEffect(() => {
    if (!bridge) return;
    const read = () => {
      void checkConnectSnapshot({ bridge }).then(
        () => {
          // Only the read's own failure clears; a refused action keeps its message.
          if (mounted.current) setError((current) => (current === LOAD_ERROR ? null : current));
        },
        () => {
          if (mounted.current) setError(LOAD_ERROR);
        },
      );
    };
    const visible = () => {
      if (document.visibilityState === "visible") read();
    };
    read();
    window.addEventListener("focus", read);
    document.addEventListener("visibilitychange", visible);
    return () => {
      window.removeEventListener("focus", read);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [bridge]);
  // Watching a change settle. Every panel showing the list watches the same snapshot, so the ones
  // that wake together share one recent answer rather than asking once each. It runs only while
  // the page is in view, stops at the first failure (Retry starts it again), and gives up after
  // the time a change normally takes: a row stuck changing is the server's to settle, not a reason
  // for an open page to ask Connect every few seconds for as long as it stays open. The timer
  // reads through a ref so a caller that rebuilds its bridge each render does not restart it.
  const watchLatest = useRef(async () => {});
  watchLatest.current = async () => {
    if (bridge)
      await checkConnectSnapshot({
        bridge,
        freshMs: CLOUD_WORKSPACE_REFRESH_MS,
        workspacesOnly: true,
      });
  };
  const keepRefreshing =
    shouldRefreshCloudWorkspaces(workspaces ?? [], requests) && error !== LOAD_ERROR;
  useEffect(() => {
    if (!keepRefreshing) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let disposed = false;
    const deadline = Date.now() + OPEN_TIMEOUT_MS;
    const schedule = () => {
      if (disposed || timer !== undefined || document.visibilityState !== "visible") return;
      if (Date.now() > deadline) return;
      timer = setTimeout(() => {
        timer = undefined;
        watchLatest.current().then(schedule, () => {
          if (!disposed) setError(LOAD_ERROR);
        });
      }, CLOUD_WORKSPACE_REFRESH_MS);
    };
    document.addEventListener("visibilitychange", schedule);
    schedule();
    return () => {
      disposed = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", schedule);
    };
  }, [keepRefreshing]);
  const setRequest = (id: string, request: CloudWorkspaceRequest | null) =>
    setRequests((current) => {
      const next = new Map(current);
      if (request) next.set(id, request);
      else next.delete(id);
      return next;
    });
  /** Runs a request, then reads the list; `request` marks its row busy until the list is read. */
  const perform = async (
    operation?: () => Promise<unknown>,
    request?: { id: string; kind: CloudWorkspaceRequest },
  ) => {
    setError(null);
    if (request) setRequest(request.id, request.kind);
    try {
      await operation?.();
      if (!mounted.current) return;
      await refresh();
    } catch (cause) {
      if (!mounted.current) return;
      setError(describeCloudFailure(cause));
      callbacks.current.onFailed?.();
    } finally {
      if (request) setRequest(request.id, null);
    }
  };
  const startAndConnect = async (workspace: CloudWorkspace) => {
    if (!bridge) return;
    const wasUsedHere = isWorkspaceUsedHere(added, workspace.id);
    const connected = await startAndConnectCloudWorkspace(bridge, workspace, {
      read: readWorkspaces,
      isActive: () => mounted.current,
    });
    if (connected && mounted.current && !wasUsedHere) callbacks.current.onAdded?.(workspace.label);
  };
  return {
    workspaces,
    allowance,
    connectable: workspaces === null ? null : workspacesToConnect(workspaces),
    requests,
    creating,
    error,
    retry: () => void perform(),
    usedHere: added ?? [],
    connect: (workspace) =>
      void perform(() => startAndConnect(workspace), { id: workspace.id, kind: "open" }),
    start: (workspace) => {
      if (!bridge) return;
      void perform(
        () =>
          bridge.cloudWorkspaceAction({
            id: workspace.id,
            action: "open",
            expectedRevision: workspace.revision,
            requestId: randomUUID(),
          }),
        { id: workspace.id, kind: "open" },
      );
    },
    act: async (workspace, kind) => {
      if (!bridge) return false;
      let accepted = false;
      await perform(
        async () => {
          await bridge.cloudWorkspaceAction({
            id: workspace.id,
            action: kind,
            expectedRevision: workspace.revision,
            requestId: randomUUID(),
          });
          accepted = true;
        },
        { id: workspace.id, kind },
      );
      return accepted;
    },
    resize: (workspace, change) => {
      if (!bridge) return;
      void perform(
        () =>
          bridge.cloudWorkspaceAction({
            id: workspace.id,
            action: "resize",
            expectedRevision: workspace.revision,
            requestId: randomUUID(),
            ...change,
          }),
        { id: workspace.id, kind: "resize" },
      );
    },
    create: async (input) => {
      if (!bridge) return false;
      let made = false;
      await perform(async () => {
        setCreating(input.label);
        try {
          await bridge.createCloudWorkspace(input);
        } finally {
          if (mounted.current) setCreating(null);
        }
        made = true;
      });
      return made;
    },
  };
}

/** One muted line: the plan, the share of usage left, and when it resets. */
export function CloudUsageLine({
  allowance,
  accountLink,
}: {
  readonly allowance: CloudAllowanceStatus;
  /** A link to Settings → Account, where the full meter lives; not before the app opens. */
  readonly accountLink?: ReactNode;
}) {
  return (
    <p className="text-xs text-muted-foreground">
      {describeCloudUsageLine(allowance)}
      {accountLink ? <> · {accountLink}</> : null}
    </p>
  );
}

function RetentionWarning({ allowance }: { readonly allowance: CloudAllowanceStatus }) {
  return (
    <p className="text-xs text-destructive">
      Out of usage, Cloud Workspaces stop until it resets. Unless an administrator adds more within{" "}
      {allowance.storageRetentionDays} days, they and their contents are permanently deleted. Push
      your work to GitHub for a durable copy.
    </p>
  );
}

function CloudError({
  cloud,
  offline,
}: {
  readonly cloud: CloudWorkspacesState;
  readonly offline: boolean;
}) {
  if (!cloud.error) return null;
  return (
    <p role="alert" className="flex items-center gap-2 text-xs text-destructive">
      {cloud.error}
      <Button size="xs" variant="outline" disabled={offline} onClick={cloud.retry}>
        Retry
      </Button>
    </p>
  );
}

/**
 * Choosing which of the account's Cloud Workspaces this device uses: each with Connect, which adds
 * it to Connections. Renting one happens on the Network page (`onRent`), where every machine lives.
 */
export function CloudWorkspacesPanel({
  bridge,
  offline,
  listConnectable,
  onAdded,
  onFailed,
  onRent,
}: {
  bridge: CloudBridge;
  offline: boolean;
  /** Opens the Network page to rent a Cloud Workspace. */
  onRent?: () => void;
  /** Whether to list the account's workspaces, each with Connect. */
  listConnectable: boolean;
  onAdded: (label: string) => void;
  onFailed?: () => void;
}) {
  const cloud = useCloudWorkspaces(bridge, { onAdded, ...(onFailed ? { onFailed } : {}) });
  const rentsHere = useNetworkBridge() !== undefined;
  const { workspaces, allowance, connectable } = cloud;
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        A Cloud Workspace is an Ubuntu computer on your AFAR Network that runs only while it has
        work. Its disk keeps your projects while it is stopped; start it to open them.
      </p>
      {allowance && <CloudUsageLine allowance={allowance} />}
      {allowance && shouldWarnAboutRetention(allowance, workspaces ?? []) && (
        <RetentionWarning allowance={allowance} />
      )}
      <CloudError cloud={cloud} offline={offline} />
      {workspaces === null || connectable === null ? (
        !cloud.error && (
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Spinner className="size-3" />
            Loading Cloud Workspaces…
          </p>
        )
      ) : (
        <>
          {listConnectable && connectable.length > 0 && (
            <ul className="space-y-2">
              {connectable.map((workspace) => {
                // Every listed row connects: one this device uses but is not connected to is
                // selected again, so it is described as the picker's row rather than a used one.
                const row = describeWorkspaceRow(workspace, {
                  usedHere: false,
                  request: cloud.requests.get(workspace.id),
                });
                return (
                  <li
                    key={workspace.id}
                    className="flex items-center gap-3 rounded-md border px-3 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm">{workspace.label}</p>
                      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        {row.busy && <Spinner className="size-3" />}
                        {row.status}
                        {allowance && <> · {describeWorkspaceSpec(allowance.catalog, workspace)}</>}
                      </p>
                    </div>
                    {row.primary && (
                      <Button
                        size="xs"
                        disabled={offline || allowance?.usageLeftPercent === 0}
                        onClick={() => cloud.connect(workspace)}
                      >
                        Connect
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {listConnectable && connectable.length === 0 && (
            <p className="text-xs text-muted-foreground">You have no Cloud Workspace yet.</p>
          )}
          {onRent &&
            // Renting is on the Network page, which only the web client has (ADR-0146).
            (rentsHere ? (
              <Button size="xs" variant="outline" disabled={offline} onClick={onRent}>
                Rent a Cloud Workspace
              </Button>
            ) : (
              <p className="text-xs text-muted-foreground">
                Rent a Cloud Workspace in AFAR on the web.
              </p>
            ))}
        </>
      )}
    </div>
  );
}

/**
 * Every size the deployment offers, cheapest first, with any that cannot be picked disabled and
 * saying why; one size alone is shown, not offered.
 */
function SizeSelect({
  allowance,
  value,
  keep,
  onChange,
}: {
  readonly allowance: CloudAllowanceStatus;
  readonly value: string;
  /** A size to offer even when the plan no longer includes it: the workspace's current one. */
  readonly keep?: string;
  readonly onChange: (size: string) => void;
}) {
  const choices = sizeChoices(allowance);
  // A deployment that offers one size leaves nothing to pick (ADR-0153): say what it is.
  const only = choices.length === 1 ? choices[0]!.size : undefined;
  if (only && only.id === value)
    return (
      <div className="text-xs">
        Size
        <p className="mt-1 py-1.5 text-sm">{describeSize(only)}</p>
      </div>
    );
  return (
    <label className="text-xs">
      Size
      <select
        className="mt-1 block rounded border bg-background p-1.5 text-sm"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        {choices.map(({ size, blocker }) => {
          const unavailable = blocker !== null && size.id !== keep;
          return (
            <option key={size.id} value={size.id} disabled={unavailable}>
              {describeSize(size)}
              {unavailable ? " · not in your plan" : ""}
            </option>
          );
        })}
      </select>
    </label>
  );
}

function DiskInput({
  allowance,
  value,
  minGb,
  onChange,
}: {
  readonly allowance: CloudAllowanceStatus;
  readonly value: string;
  readonly minGb: number;
  readonly onChange: (value: string) => void;
}) {
  return (
    <label className="text-xs">
      Disk (GB)
      <input
        type="number"
        inputMode="numeric"
        className="mt-1 block w-24 rounded border bg-background p-1.5 text-sm"
        min={minGb}
        max={allowance.catalog.disk.maxGb}
        step={1}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

/** Changing a stopped workspace's size and growing its disk. */
export function ResizeCloudWorkspace({
  allowance,
  workspace,
  disabled,
  onResize,
  onCancel,
}: {
  readonly allowance: CloudAllowanceStatus;
  readonly workspace: CloudWorkspace;
  readonly disabled: boolean;
  readonly onResize: (change: { size?: string; diskGb?: number }) => void;
  readonly onCancel: () => void;
}) {
  const [size, setSize] = useState(workspace.size);
  const [disk, setDisk] = useState(String(workspace.diskGb));
  const next = { size, diskGb: Number(disk) };
  const blocker = cloudResizeBlocker({ allowance, workspace, ...next });
  return (
    <form
      className="space-y-2 rounded-md border px-3 py-2.5"
      onSubmit={(event) => {
        event.preventDefault();
        if (blocker === null) onResize(resizeChange(workspace, next));
      }}
    >
      <div className="flex flex-wrap items-end gap-2">
        <SizeSelect allowance={allowance} value={size} keep={workspace.size} onChange={setSize} />
        <DiskInput allowance={allowance} value={disk} minGb={workspace.diskGb} onChange={setDisk} />
      </div>
      {blocker && <p className="text-xs text-muted-foreground">{blocker}</p>}
      <div className="flex gap-2">
        <Button type="submit" size="xs" disabled={disabled || blocker !== null}>
          Resize
        </Button>
        <Button type="button" size="xs" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/**
 * "New Cloud Workspace": a button that opens the name, image, size and disk form, or one line
 * saying why the plan does not allow another.
 */
export function CreateCloudWorkspace({
  allowance,
  workspaceCount,
  initiallyOpen,
  disabled,
  creating,
  onCreate,
}: {
  allowance: CloudAllowanceStatus;
  workspaceCount: number;
  initiallyOpen: boolean;
  disabled: boolean;
  creating: string | null;
  onCreate: CloudWorkspacesState["create"];
}) {
  const { catalog } = allowance;
  const [open, setOpen] = useState(initiallyOpen);
  const [label, setLabel] = useState("My Cloud Workspace");
  const [chosenImage, setImage] = useState(catalog.images[0]?.id ?? "");
  const [chosenSize, setSize] = useState(() => defaultSize(allowance));
  const [disk, setDisk] = useState(String(catalog.disk.defaultGb));
  // The rentable sizes come from the provider and can change after the form opened, as when a
  // cached list gives way to a fresh one: a choice no longer listed falls back to the default
  // rather than leaving the form holding a size the picker cannot show.
  const image = catalog.images.some((entry) => entry.id === chosenImage)
    ? chosenImage
    : (catalog.images[0]?.id ?? "");
  const size = catalog.sizes.some((entry) => entry.id === chosenSize)
    ? chosenSize
    : defaultSize(allowance);
  // A retried create reuses its request id, so Connect answers with the workspace it already made.
  const pending = useRef<CreateRequest | null>(null);
  const accountBlocker = cloudCreateBlocker({ allowance, workspaceCount });
  const spec = { image, size, diskGb: Number(disk) };
  const specBlocker = cloudCreateBlocker({ allowance, workspaceCount, spec });

  if (creating)
    return (
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Spinner className="size-3" />
        Creating {creating}…
      </p>
    );
  if (accountBlocker) return <p className="text-xs text-muted-foreground">{accountBlocker}</p>;
  if (!open)
    return (
      <Button size="xs" variant="outline" disabled={disabled} onClick={() => setOpen(true)}>
        <PlusIcon />
        New Cloud Workspace
      </Button>
    );
  const trimmed = label.trim();
  const selectedImage = catalog.images.find((entry) => entry.id === image);
  return (
    <form
      className="space-y-2 rounded-md border px-3 py-2.5"
      onSubmit={(event) => {
        event.preventDefault();
        const wanted = { label: trimmed, ...spec };
        if (!pending.current || !sameCreateRequest(pending.current, wanted))
          pending.current = { ...wanted, requestId: randomUUID() };
        const request = pending.current;
        void onCreate(request).then((made) => {
          if (made && pending.current === request) pending.current = null;
        });
      }}
    >
      <p className="text-sm font-medium">New Cloud Workspace</p>
      <div className="flex flex-wrap items-end gap-2">
        <label className="min-w-48 flex-1 text-xs">
          Name
          <input
            className="mt-1 w-full rounded border bg-background p-1.5 text-sm"
            value={label}
            placeholder="My Cloud Workspace"
            onChange={(event) => setLabel(event.target.value)}
          />
        </label>
        <label className="text-xs">
          Image
          <select
            className="mt-1 block rounded border bg-background p-1.5 text-sm"
            value={image}
            onChange={(event) => setImage(event.target.value)}
          >
            {catalog.images.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.label}
              </option>
            ))}
          </select>
        </label>
        <SizeSelect allowance={allowance} value={size} onChange={setSize} />
        <DiskInput
          allowance={allowance}
          value={disk}
          minGb={catalog.disk.minGb}
          onChange={setDisk}
        />
      </div>
      {selectedImage && (
        <p className="text-xs text-muted-foreground">
          {selectedImage.description} The image is fixed;{" "}
          {catalog.sizes.length > 1 ? "the size can change and " : ""}the disk can grow while the
          workspace is stopped.
        </p>
      )}
      {specBlocker && <p className="text-xs text-muted-foreground">{specBlocker}</p>}
      <div className="flex gap-2">
        <Button
          type="submit"
          size="xs"
          disabled={disabled || specBlocker !== null || trimmed.length === 0}
        >
          Create
        </Button>
        {!initiallyOpen && (
          <Button type="button" size="xs" variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}

type CreateRequest = Parameters<CloudWorkspacesState["create"]>[0];
const sameCreateRequest = (a: CreateRequest, b: Omit<CreateRequest, "requestId">) =>
  a.label === b.label && a.image === b.image && a.size === b.size && a.diskGb === b.diskGb;
